import React from 'react'

import { Box, Typography } from '@mui/material'
import { ClipboardList, Package, Ruler, Scale, TriangleAlert } from 'lucide-react'

import { stripPrefixFromId } from '../../../../shared/functions/ID/id.functions'
import { formatDimensionsCm, formatWeightLbs } from '../../recipientTracking.functions'
import styles, { getDesktopItemImgBoxStyle } from './OrderCard.styles'
import type { OrderCardProps } from './OrderCard.types'

const OrderCard: React.FC<OrderCardProps> = (props) => {
  const { orderId, items, package: orderPackage } = props

  const weightFromPackage =
    orderPackage != null && orderPackage.totalWeightGrams != null && orderPackage.totalWeightGrams > 0
      ? orderPackage.totalWeightGrams
      : null
  const weightFromItems = items.reduce(
    (sum: number, item: OrderCardProps['items'][number]) => sum + (item.weightGrams ?? 0),
    0
  )
  const totalWeightGrams = weightFromPackage ?? weightFromItems
  const packageWeightDisplay = totalWeightGrams > 0 ? formatWeightLbs(totalWeightGrams) : '—'

  const hasPackageDimensions =
    orderPackage != null &&
    orderPackage.lengthCm != null &&
    orderPackage.widthCm != null &&
    orderPackage.heightCm != null
  const firstItem = items[0]
  const hasFirstItemDimensions =
    firstItem != null && firstItem.lengthCm != null && firstItem.widthCm != null && firstItem.heightCm != null
  const packageDimensionsDisplay = hasPackageDimensions
    ? formatDimensionsCm(orderPackage.lengthCm, orderPackage.widthCm, orderPackage.heightCm)
    : hasFirstItemDimensions && firstItem != null
      ? formatDimensionsCm(Number(firstItem.lengthCm), Number(firstItem.widthCm), Number(firstItem.heightCm))
      : '—'

  const hasFragile = items.some((item: OrderCardProps['items'][number]) => item.fragile)

  return (
    <Box sx={styles.card}>
      {/* Header — always visible */}
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderLeft}>
          <Typography sx={styles.cardTitle}>Order Details</Typography>
          <Typography sx={styles.cardTitleOrderId}>&nbsp;· {stripPrefixFromId(orderId)}</Typography>
        </Box>

        <Box sx={styles.iconWrap}>
          <ClipboardList size={15} color="#339FFF" />
        </Box>
      </Box>

      {/* Desktop only: chips row */}
      <Box sx={styles.chipsRow}>
        {totalWeightGrams > 0 && (
          <Box sx={styles.chip}>
            <Scale size={13} color="#6B7280" />
            <Typography sx={styles.chipText}>{packageWeightDisplay}</Typography>
          </Box>
        )}

        {packageDimensionsDisplay !== '—' && (
          <Box sx={styles.chip}>
            <Ruler size={13} color="#6B7280" />
            <Typography sx={styles.chipText}>{packageDimensionsDisplay}</Typography>
          </Box>
        )}

        {hasFragile && (
          <Box sx={styles.fragileChip}>
            <TriangleAlert size={13} color="#F59E0B" />
            <Typography sx={styles.fragileChipText}>Fragile</Typography>
          </Box>
        )}
      </Box>

      {/* Desktop only: divider + items section */}
      <Box sx={styles.desktopDivider} />

      <Box sx={styles.itemsSubHeader}>
        <Typography sx={styles.itemsSubHeaderTitle}>Items in this Order</Typography>

        <Box sx={styles.itemsBadge}>
          <Typography sx={styles.itemsBadgeText}>
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.desktopItemsWrapper}>
        {items.map((item: OrderCardProps['items'][number], index: number) => {
          const imageUrl = item.imageUrls?.[0]

          return (
            <React.Fragment key={item.itemId}>
              {index > 0 && <Box sx={styles.desktopItemDivider} />}

              <Box sx={styles.desktopItemRow}>
                <Box sx={getDesktopItemImgBoxStyle(index)}>
                  {imageUrl != null ? (
                    <img
                      src={imageUrl}
                      alt={item.description}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                    />
                  ) : (
                    <Package size={24} color="#6B7280" />
                  )}
                </Box>

                <Box sx={styles.desktopItemInfo}>
                  <Typography sx={styles.desktopItemTitle}>{item.description}</Typography>

                  {item.fragile === true && (
                    <Box sx={styles.desktopItemFragileChip}>
                      <TriangleAlert size={12} color="#F59E0B" />
                      <Typography sx={styles.desktopItemFragileChipText}>Fragile</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
}

export default OrderCard
